import { useEffect, useRef } from 'react';

interface CTASectionProps {
  onParticipate: () => void;
  onSponsor: () => void;
}

export default function CTASection({ onParticipate, onSponsor }: CTASectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-target').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 160);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="inscricao"
      ref={sectionRef}
      className="relative py-32 md:py-40 px-6 overflow-hidden bg-obsidian"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/589449273c3832d37a749afc7191a838.jpg"
          alt="Espaço do evento"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-obsidian/85" />
      </div>

      {/* Top gold line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gold/30 z-10" />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Tag */}
        <div className="animate-target opacity-0 transition-all duration-700 mb-8" style={{ transform: 'translateY(30px)' }}>
          <span className="font-sans text-xs tracking-widest3 uppercase text-gold" style={{ letterSpacing: '0.35em' }}>
            14–16 Ago 2026 &nbsp;|&nbsp; São Paulo · Alphaville
          </span>
        </div>

        {/* Gold line */}
        <div className="animate-target opacity-0 transition-all duration-700 w-16 h-px bg-gold mx-auto mb-10" style={{ transform: 'translateY(30px)' }} />

        {/* Title */}
        <div className="animate-target opacity-0 transition-all duration-700 mb-8" style={{ transform: 'translateY(30px)' }}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-cream leading-tight">
            O rugido é coletivo.
            <br />
            <span className="italic text-gold">Mas a decisão é sua.</span>
          </h2>
        </div>

        {/* Quote */}
        <div className="animate-target opacity-0 transition-all duration-700 mb-14 max-w-2xl mx-auto" style={{ transform: 'translateY(30px)' }}>
          <p className="font-serif italic text-cream/50 text-base md:text-lg leading-relaxed">
            "Na savana, o leão ruge para intimidar. A leoa ruge para alimentar. Nós rugimos para construir."
          </p>
        </div>

        {/* CTAs */}
        <div className="animate-target opacity-0 transition-all duration-700 flex flex-col sm:flex-row gap-4 justify-center items-center" style={{ transform: 'translateY(30px)' }}>
          <button
            onClick={onParticipate}
            className="font-sans text-xs tracking-widest uppercase bg-gold text-obsidian px-12 py-4 hover:bg-gold-dark transition-all duration-300 cursor-pointer whitespace-nowrap font-semibold"
            style={{ letterSpacing: '0.2em' }}
          >
            Quero Participar
          </button>
          <button
            onClick={onSponsor}
            className="font-sans text-xs tracking-widest uppercase border border-gold/60 text-gold px-12 py-4 hover:border-gold hover:bg-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ letterSpacing: '0.2em' }}
          >
            Quero Patrocinar
          </button>
        </div>
      </div>

      {/* Bottom gold line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gold/30 z-10" />
    </section>
  );
}
